import { createServerFn } from "@tanstack/react-start";
import { asc } from "drizzle-orm";
import { db } from "~/db";
import { hotelType } from "~/db/schema/localground/hoteltype.schema";
import z from "zod";

export type HotelType = {
  id: number;
  name: string;
};

const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes
let cachedHotelTypes: { data: HotelType[]; timestamp: number } | null = null;

const GetHotelTypesSchema = z.object({
  ids: z.array(z.number()).optional(),
  names: z.array(z.string()).optional(),
});

export const getHotelTypes = createServerFn({ method: "GET" })
  .inputValidator(GetHotelTypesSchema.optional())
  .handler(
    async (ctx) => {
      const ids = ctx.data?.ids;
      const names = ctx.data?.names;

      const now = Date.now();
      if (
        cachedHotelTypes &&
        now - cachedHotelTypes.timestamp < CACHE_TTL_MS
      ) {
        console.log("Serving hotel types from cache.");
        const types = cachedHotelTypes.data;

        // If names are provided, convert them to IDs first
        let finalIds = ids;
        if (names && names.length > 0) {
          const nameMap = new Map(types.map((type) => [type.name, type.id]));
          finalIds = names
            .map((name) => nameMap.get(name))
            .filter((id): id is number => id !== undefined);
        }

        // If IDs are provided (either directly or converted from names), filter from cache and preserve order
        if (finalIds && finalIds.length > 0) {
          const typeMap = new Map(types.map((type) => [type.id, type]));
          return finalIds
            .map((id) => typeMap.get(id))
            .filter((type): type is HotelType => type !== undefined);
        }

        return types;
      }
      if (
        cachedHotelTypes &&
        now - cachedHotelTypes.timestamp >= CACHE_TTL_MS
      ) {
        cachedHotelTypes = null;
      }

      console.log("Cache miss. Fetching hotel types from database...");
      const allTypes = await db
        .select({
          id: hotelType.id,
          name: hotelType.name,
        })
        .from(hotelType)
        .groupBy(hotelType.id, hotelType.name)
        .orderBy(asc(hotelType.name));

      cachedHotelTypes = { data: allTypes, timestamp: Date.now() };

      // If names are provided, convert them to IDs first
      let finalIds = ids;
      if (names && names.length > 0) {
        const nameMap = new Map(
          allTypes.map((type) => [type.name, type.id])
        );
        finalIds = names
          .map((name) => nameMap.get(name))
          .filter((id): id is number => id !== undefined);
      }

      // If IDs are provided (either directly or converted from names), filter the results and preserve order
      if (finalIds && finalIds.length > 0) {
        const typeMap = new Map(
          allTypes.map((type) => [type.id, type])
        );
        return finalIds
          .map((id) => typeMap.get(id))
          .filter((type): type is HotelType => type !== undefined);
      }

      return allTypes;
    }
  );
